import useTranslation from 'hooks/useTranslation'
import { displayPrice } from 'utils/string'
import React, { useEffect, useRef } from 'react'
import type { ListRow } from './utils'

interface ListItemProps {
  row: ListRow
  isSelected: boolean
  select: (row: ListRow) => void
}

const toMeters = (...values: Array<number | string | undefined>) => {
  for (const value of values) {
    const number = Number(value)
    if (value !== undefined && value !== '' && number > 0) return number
  }
}

const ListItem: React.FC<ListItemProps> = ({ row, isSelected, select }) => {
  const ref = useRef<HTMLDivElement>(null)
  const { t } = useTranslation('placeMap')
  const { group, number, isAvailable, isAvailableNow, availableFrom } = row
  const details = group.data

  // Po przełączeniu na listę (lub wyborze z zewnątrz) przewiń do wybranego magazynu
  useEffect(() => {
    if (isSelected) {
      ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [isSelected])

  const area = toMeters(details?.area)
  const attributes = [
    { key: 'fullscreen', label: t('area-short'), value: area !== undefined ? <>{area}m<sup>2</sup></> : undefined },
    { key: 'length', label: t('length-short'), value: toMeters(details?.internalLength, details?.length) },
    { key: 'width', label: t('width-short'), value: toMeters(details?.internalWidth, details?.width) },
    { key: 'height', label: t('height-short'), value: toMeters(details?.internalHeight, details?.height) },
  ].filter(({ value }) => value !== undefined)

  return (
    <div
      ref={ref}
      className={`placeMap__list__item${isSelected ? ' --selected' : ''}${isAvailable ? '' : ' --disabled'}`}
      onClick={() => {
        if (isAvailable && !isSelected) select(row)
      }}>
      <div className='placeMap__list__item__title --secFont'>
        {number !== undefined ? `${t('no')} ${number}` : group.name}
      </div>

      <div className='placeMap__list__item__attributes'>
        {attributes.map(({ key, label, value }) => <div
          key={key}
          className='placeMap__pill'>
          <i className={`icon icon--${key}`} />
          <span>{label}: {value}{key === 'fullscreen' ? '' : 'm'}</span>
        </div>)}
        {isAvailable && !isAvailableNow && availableFrom && <div className='placeMap__pill --warning'>
          <span>{t('availableFrom')} {availableFrom}</span>
        </div>}
      </div>

      <div className='placeMap__list__item__right'>
        <div className='placeMap__list__item__price'>
          <div className='--secFont'>{displayPrice(details?.monthlyGrossPrice || 0)}</div>
          <div>{t('monthly')}</div>
        </div>
        <button
          type='button'
          disabled={!isAvailable}
          className={`button placeMap__list__item__button${isSelected ? ' --selected' : ''}`}
          onClick={e => {
            e.stopPropagation()
            if (isAvailable && !isSelected) select(row)
          }}>
          {!isAvailable ? t('inaccessible') : isSelected ? t('chosen') : t('select')}
        </button>
      </div>
    </div>
  )
}

export default ListItem
